import { localResources, type ResourceCategory, type WebResource } from '@/content/resources';
import {
  categorySlug as hubCategorySlug,
  hubSectionFromSlug as hubSectionFromTitleSlug,
  POPULAR_SECTION_TITLE,
  type HubSection,
} from '@/lib/hub-sections';

const GAME_HREF: Record<string, string> = {
  '/(games)/game-match-numbers': '/app/games/match-numbers',
  '/(games)/game-match-family': '/app/games/match-family',
  '/(games)/game-spell-numbers': '/app/games/spell-numbers',
  '/(games)/game-spell-family': '/app/games/spell-family',
  '/(games)/game-animal-guesser': '/app/games/animal-guesser',
  '/(games)/match-the-body-parts': '/app/games/body-parts',
  '/(games)/game-build-sentence': '/app/games/build-sentence',
  '/(games)/game-sentence-practice': '/app/games/practice',
  '/(games)/game-flappy-hmong': '/app/games/ya-ya',
  '/(games)/game-snake': '/app/games/nab',
};

const STORY_HREF: Record<string, string> = {
  alphabet: '/app/storybooks/alphabet',
  halloween: '/app/storybooks/halloween',
  mai: '/app/storybooks/mai',
  hmong_meaning: '/app/storybooks/hmong-meaning',
  sky_high: '/app/storybooks/sky-high',
  kuv_sov_sov: '/app/storybooks/kuv-sov-sov',
};

const GAME_KEY = Object.fromEntries(Object.entries(GAME_HREF).map(([key, href]) => [href, key]));
const STORY_KEY = Object.fromEntries(
  Object.entries(STORY_HREF).map(([id, href]) => [href, `/(storybooks)/storybook?storybook=${id}`]),
);

const KNOWN_HREFS = new Set(localResources.map((resource) => resource.href));

export { POPULAR_SECTION_TITLE };
export const POPULAR_SECTION_SLUG = 'popular';
export type { HubSection };

/** Village is mobile-only; never surface it on the web hub. */
export function isVillageResourceKey(key: string): boolean {
  return /village|hmong-village/i.test(key);
}

function isKnownWebHref(href: string, extras: readonly WebResource[]): boolean {
  return KNOWN_HREFS.has(href) || extras.some((resource) => resource.href === href);
}

/**
 * Turn a mobile `modalLink` / tap `resource_key` into a candidate web route.
 * Availability is checked separately against the local + cloud catalog.
 */
function candidateWebHrefFromResourceKey(key: string | null | undefined): string | null {
  if (!key) return null;
  const trimmed = key.trim();
  if (!trimmed || isVillageResourceKey(trimmed)) return null;
  if (trimmed.startsWith('/app/')) return trimmed;

  const [path, search = ''] = trimmed.split('?');
  const params = new URLSearchParams(search);

  if (path === '/(flashcards)/favorite-flashcards') return '/app/flashcards/favorites';
  if (path.startsWith('/(stickers)')) return '/app/stickers';

  const cloudFlash = path.match(/\/\(flashcards\)\/cloud\/([a-z0-9-]+)/i);
  if (cloudFlash) return `/app/flashcards/${cloudFlash[1].toLowerCase()}`;

  const flash = path.match(/\/\(flashcards\)\/([a-z0-9-]+)-flashcards/i);
  if (flash) {
    const slug = flash[1].toLowerCase();
    if (slug === 'numbers') {
      const range = params.get('numberRange') || '1-10';
      return `/app/flashcards/numbers?range=${range}`;
    }
    return `/app/flashcards/${slug}`;
  }

  const cloudSheet = path.match(/\/\(cheatsheets\)\/cloud\/([a-z0-9-]+)/i);
  if (cloudSheet) return `/app/sheets/${cloudSheet[1].toLowerCase()}`;

  const sheet = path.match(/\/\(cheatsheets\)\/([a-z0-9-]+)/i);
  if (sheet) {
    const slug = sheet[1].toLowerCase();
    if (slug === 'useful-terms') {
      return `/app/sheets/useful-terms?terms=${params.get('terms') || 'words'}`;
    }
    if (slug === 'word-families') {
      return `/app/sheets/word-families?family=${params.get('family') || 'a'}`;
    }
    return `/app/sheets/${slug}`;
  }

  const song = trimmed.match(/[?&]songId=([a-z0-9_]+)/i);
  if (song) return `/app/songs/${song[1].toLowerCase().replace(/_/g, '-')}`;

  const cloudStory = path.match(/\/\(storybooks\)\/cloud\/([a-z0-9-]+)/i);
  if (cloudStory) return `/app/storybooks/${cloudStory[1].toLowerCase()}`;

  const story = trimmed.match(/[?&]storybook=([a-z0-9_]+)/i);
  if (story) {
    const id = story[1].toLowerCase();
    return STORY_HREF[id] ?? `/app/storybooks/${id.replace(/_/g, '-')}`;
  }

  const diagram = path.match(/\/\(diagrams\)\/([a-z0-9-]+)/i);
  if (diagram) return `/app/diagrams/${diagram[1].toLowerCase()}`;

  if (trimmed.includes('crosswords')) return '/app/crosswords';

  return GAME_HREF[path] ?? GAME_HREF[trimmed] ?? null;
}

export function webHrefFromResourceKey(
  key: string | null | undefined,
  extras: readonly WebResource[] = [],
): string | null {
  const href = candidateWebHrefFromResourceKey(key);
  if (!href) return null;
  return isKnownWebHref(href, extras) ? href : null;
}

export function resourceKeyForWebResource(resource: WebResource): string {
  if (resource.mobileKey) return resource.mobileKey;
  const href = resource.href.split('?')[0];
  if (GAME_KEY[href]) return GAME_KEY[href];
  if (STORY_KEY[href]) return STORY_KEY[href];
  const bundled = KNOWN_HREFS.has(resource.href) || KNOWN_HREFS.has(href);

  if (href.startsWith('/app/flashcards/')) {
    return bundled ? `/(flashcards)/${resource.slug}-flashcards` : `/(flashcards)/cloud/${resource.slug}`;
  }
  if (href.startsWith('/app/sheets/')) {
    return bundled ? `/(cheatsheets)/${resource.slug}` : `/(cheatsheets)/cloud/${resource.slug}`;
  }
  if (href.startsWith('/app/storybooks/')) {
    return `/(storybooks)/cloud/${resource.slug}`;
  }
  if (href.startsWith('/app/songs/')) {
    return `/(songs)/song?songId=${resource.slug.replace(/-/g, '_')}`;
  }
  if (href.startsWith('/app/diagrams/')) {
    return `/(diagrams)/${resource.slug}`;
  }
  if (href.includes('crosswords')) return '/(crosswords)/daily';
  if (href.startsWith('/app/stickers')) return '/(stickers)/sticker-packs';
  return resource.slug;
}

export function resourceForHref(href: string, extras: WebResource[] = []): WebResource | undefined {
  return extras.find((item) => item.href === href) ?? localResources.find((item) => item.href === href);
}

export function categorySlug(category: ResourceCategory | string): string {
  return hubCategorySlug(category as ResourceCategory);
}

export function categoryFromSlug(slug: string | undefined): ResourceCategory | null {
  if (!slug) return null;
  const match = (['Flashcards', 'Games', 'Storybooks', 'Grammar', 'Pronunciation', 'Songs', 'Puzzles'] as const).find(
    (category) => category.toLowerCase() === slug.toLowerCase(),
  );
  return match ?? null;
}

export function hubSectionFromSlug(slug: string | undefined): HubSection | null {
  return hubSectionFromTitleSlug(slug);
}
